import {
  RANGER_WEAPONS,
  advanceProjectilePosition,
  createAimedProjectile,
  createWeaponProjectiles,
  getDistanceBetweenPoints,
  hasLineOfSight,
  normalizeVector,
  projectileIsOutsideBounds,
} from '../rangerLogic';
import { ENEMY_SHOT_SPEED } from '../rangerTuning';
import {
  createCameraBump,
  createDamageFlash,
  createDefeatDebris,
  createExplosionEffect,
  createImpactEffect,
  createMeleeEffect,
  createMuzzleFlash,
  createScorePopup,
  playRangerSound,
  showToast,
} from './missionEffects';
import { setEnemyAwareness, spreadEnemyAlert } from './missionEnemies';

function createProjectileSprite(scene, projectile) {
  let sprite;

  if (projectile.weaponType === 'grenade') {
    sprite = scene.add.circle(projectile.x, projectile.y, projectile.radius, 0x3d4b2d, 1)
      .setStrokeStyle(2, 0xd8c27c, 0.82)
      .setDepth(11);
  } else {
    sprite = scene.add.rectangle(
      projectile.x,
      projectile.y,
      projectile.width,
      projectile.height,
      0xffe08a,
      1
    )
      .setStrokeStyle(1, 0x6f4a17, 0.6)
      .setDepth(11);
  }

  sprite.setRotation(projectile.angle ?? 0);
  scene.physics.add.existing(sprite);
  sprite.body.setAllowGravity(false);
  sprite.body.setVelocity(0, 0);
  sprite.setData('vx', projectile.vx);
  sprite.setData('vy', projectile.vy);
  sprite.setData('weaponType', projectile.weaponType);
  sprite.setData('damage', projectile.damage);
  sprite.setData('blastRadius', projectile.blastRadius);
  scene.playerProjectiles.add(sprite);

  scene.time.delayedCall(projectile.lifetimeMs, () => {
    if (!sprite.scene) {
      return;
    }

    if (projectile.weaponType === 'grenade') {
      explodeGrenade(scene, sprite);
      return;
    }

    sprite.destroy();
  });

  return sprite;
}

function damageEnemy(scene, enemy, damage, sourcePosition) {
  const data = enemy.enemyData;

  if (data.defeated) {
    return;
  }

  data.health = Math.max(0, (data.health ?? 2) - damage);
  setEnemyAwareness(enemy, 'attacking', scene, { x: scene.player.x, y: scene.player.y });
  spreadEnemyAlert(scene, enemy);
  data.nextShotTime = Math.min(data.nextShotTime ?? 0, scene.time.now + 260);
  enemy.setAlpha(0.7);
  createDamageFlash(scene, enemy, 0xffefad);
  createImpactEffect(scene, sourcePosition.x, sourcePosition.y, 0xffe0a3, 16);
  createCameraBump(scene, 'enemyHit');
  playRangerSound('bossHit');

  if (scene.player) {
    const staggerVector = normalizeVector({
      x: enemy.x - scene.player.x,
      y: enemy.y - scene.player.y,
    });
    enemy.setPosition(enemy.x + staggerVector.x * 4, enemy.y + staggerVector.y * 4);
    enemy.body?.updateFromGameObject?.();
  }

  scene.time.delayedCall(110, () => {
    if (enemy.scene && !data.defeated) {
      enemy.setAlpha(1);
    }
  });

  if (data.health > 0) {
    return;
  }

  data.defeated = true;
  scene.enemyDefeatCount += 1;
  enemy.body.setVelocity(0, 0);
  enemy.body.enable = false;
  enemy.setAlpha(0.34);
  enemy.enemyData.visionGraphic?.clear();
  enemy.enemyData.alertMarker?.setVisible(false);
  createExplosionEffect(scene, enemy.x, enemy.y, 34);
  createDefeatDebris(scene, enemy.x, enemy.y);
  createScorePopup(scene, enemy.x, enemy.y - 34, '+90', '#ffe7a3');
  showToast(scene, 'Patrol neutralized +90');
}

export function explodeGrenade(scene, grenade) {
  if (!grenade.scene) {
    return;
  }

  const blastPosition = { x: grenade.x, y: grenade.y };
  const blastRadius = grenade.getData('blastRadius') ?? RANGER_WEAPONS.grenade.blastRadius;

  createExplosionEffect(scene, blastPosition.x, blastPosition.y, blastRadius);
  createCameraBump(scene, 'grenade');
  playRangerSound('explosion');
  scene.enemies?.forEach((enemy) => {
    if (enemy.enemyData.defeated) {
      return;
    }

    if (getDistanceBetweenPoints(blastPosition, enemy) <= blastRadius) {
      damageEnemy(scene, enemy, grenade.getData('damage') ?? 2, blastPosition);
    }
  });
  grenade.destroy();
}

export function firePlayerWeapon(scene, weaponType, aimVector) {
  const projectiles = createWeaponProjectiles({
    weaponType,
    origin: { x: scene.player.x, y: scene.player.y },
    facingDirection: scene.playerFacingDirection,
    aimVector,
    projectileIdStart: scene.nextProjectileId,
  });

  scene.nextProjectileId += projectiles.length;
  createMuzzleFlash(scene, weaponType, aimVector);
  createCameraBump(scene, weaponType);
  playRangerSound(weaponType === 'grenade' ? 'powerup' : 'laser');
  projectiles.forEach((projectile) => createProjectileSprite(scene, projectile));
}

export function executePlayerMelee(scene, aimVector) {
  const melee = RANGER_WEAPONS.melee;
  const origin = { x: scene.player.x, y: scene.player.y };
  let hitCount = 0;

  createMeleeEffect(scene, aimVector);
  createCameraBump(scene, 'melee');
  playRangerSound('laser');
  scene.enemies?.forEach((enemy) => {
    if (enemy.enemyData.defeated) {
      return;
    }

    const distance = getDistanceBetweenPoints(origin, enemy);

    if (distance > melee.range) {
      return;
    }

    const enemyVector = normalizeVector({
      x: enemy.x - origin.x,
      y: enemy.y - origin.y,
    });
    const dot = aimVector.x * enemyVector.x + aimVector.y * enemyVector.y;
    const maxDelta = Math.cos(((melee.angleDegrees ?? 86) * Math.PI / 180) / 2);

    if (dot < maxDelta || !hasLineOfSight(origin, enemy, scene.visionBlockers)) {
      return;
    }

    hitCount += 1;
    damageEnemy(scene, enemy, scene.playerClassConfig?.meleeDamage ?? melee.damage, {
      x: enemy.x,
      y: enemy.y,
    });
  });

  if (hitCount > 0) {
    createScorePopup(scene, origin.x, origin.y - 30, 'Bayonet hit', '#fff0ad');
  }
  showToast(scene, hitCount > 0 ? 'Bayonet hit' : 'Bayonet sweep');
}

function resolveProjectileEnemyPair(firstObject, secondObject) {
  const firstIsProjectile = Boolean(firstObject?.getData?.('weaponType'));
  const secondIsProjectile = Boolean(secondObject?.getData?.('weaponType'));

  if (firstIsProjectile && secondObject?.enemyData) {
    return { projectile: firstObject, enemy: secondObject };
  }

  if (secondIsProjectile && firstObject?.enemyData) {
    return { projectile: secondObject, enemy: firstObject };
  }

  return { projectile: null, enemy: null };
}

export function handlePlayerProjectileHit(scene, firstObject, secondObject) {
  const { projectile, enemy } = resolveProjectileEnemyPair(firstObject, secondObject);

  if (!projectile?.scene || !enemy?.enemyData || enemy.enemyData.defeated) {
    return;
  }

  if (projectile.getData('weaponType') === 'grenade') {
    explodeGrenade(scene, projectile);
    return;
  }

  damageEnemy(scene, enemy, projectile.getData('damage') ?? 1, {
    x: projectile.x,
    y: projectile.y,
  });
  projectile.destroy();
}

export function createEnemyShot(scene, enemy) {
  const shot = createAimedProjectile({
    origin: { x: enemy.x, y: enemy.y },
    target: { x: scene.player.x, y: scene.player.y },
    speed: enemy.enemyData.shotSpeed ?? ENEMY_SHOT_SPEED,
  });
  const sprite = scene.add.rectangle(enemy.x, enemy.y, 11, 5, 0xff825d, 1)
    .setStrokeStyle(1, 0x4c1e16, 0.7)
    .setDepth(11);
  const angle = Math.atan2(shot.vy, shot.vx);

  sprite.setRotation(angle);
  scene.physics.add.existing(sprite);
  sprite.body.setAllowGravity(false);
  sprite.body.setVelocity(0, 0);
  sprite.setData('vx', shot.vx);
  sprite.setData('vy', shot.vy);
  scene.enemyProjectiles.add(sprite);
  createImpactEffect(scene, enemy.x, enemy.y, 0xffb070, 10);
  playRangerSound('laser');

  scene.time.delayedCall(1200, () => {
    sprite.destroy();
  });
}

function updateProjectileGroup(scene, projectileGroup, deltaMs) {
  projectileGroup?.getChildren().forEach((projectile) => {
    if (!projectile.scene) {
      return;
    }

    const nextPosition = advanceProjectilePosition({
      x: projectile.x,
      y: projectile.y,
      vx: projectile.getData('vx') ?? 0,
      vy: projectile.getData('vy') ?? 0,
    }, deltaMs);

    projectile.setPosition(nextPosition.x, nextPosition.y);
    projectile.body?.updateFromGameObject?.();

    if (projectile.getData('weaponType') === 'grenade') {
      projectile.setRotation(projectile.rotation + deltaMs * 0.012);
    }

    if (!projectileIsOutsideBounds(projectile, scene.level)) {
      return;
    }

    if (projectile.getData('weaponType') === 'grenade') {
      explodeGrenade(scene, projectile);
      return;
    }

    projectile.destroy();
  });
}

export function updateProjectiles(scene, deltaMs) {
  updateProjectileGroup(scene, scene.playerProjectiles, deltaMs);
  updateProjectileGroup(scene, scene.enemyProjectiles, deltaMs);
}

export function freezeMissionScene(scene) {
  scene.player?.body?.setVelocity(0, 0);
  scene.enemies?.forEach((enemy) => enemy.body?.setVelocity(0, 0));
}

function showGameOver(scene) {
  if (scene.isGameOver) {
    return;
  }

  scene.isGameOver = true;
  scene.player.body.setVelocity(0, 0);
  scene.physics.world.pause();
  playRangerSound('damage');
  scene.add.text(scene.level.width / 2, scene.level.height / 2, 'Game Over', {
    fontFamily: 'monospace',
    fontSize: '30px',
    color: '#fff0bd',
    stroke: '#3f2018',
    strokeThickness: 7,
  }).setOrigin(0.5).setDepth(22);
  scene.onGameOver?.();
}

export function damagePlayer(scene, message = 'Incoming fire. Back to the LZ.') {
  if (scene.isGameOver || scene.time.now - (scene.lastPlayerDamageTime ?? 0) < (scene.playerClassConfig?.damageResetMs ?? 850)) {
    return;
  }

  scene.lastPlayerDamageTime = scene.time.now;
  scene.alertCount += 1;
  scene.playerLives = Math.max(0, scene.playerLives - 1);
  scene.onLivesChange?.(scene.playerLives);
  createImpactEffect(scene, scene.player.x, scene.player.y, 0xffbd7b, 22);
  createDamageFlash(scene, scene.player, 0xff8d6b);
  createCameraBump(scene, 'playerDamage');
  playRangerSound('damage');

  if (scene.playerLives <= 0) {
    showToast(scene, 'No lives left.');
    showGameOver(scene);
    return;
  }

  scene.player.setPosition(scene.level.playerSpawn.x, scene.level.playerSpawn.y);
  scene.player.body.setVelocity(0, 0);
  createScorePopup(scene, scene.player.x, scene.player.y - 30, `Lives ${scene.playerLives}`, '#ffd2c7');
  showToast(scene, message);
}
